import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import LoyaltyAccountStatusSummary from 'loyalty/components/LoyaltyAccountStatusSummary'
import Divider from 'LuxKit/Divider'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import useCurrentLoyaltyActivities from 'loyalty/hooks/useCurrentLoyaltyActivities'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPointsDeductionInfoModal from 'loyalty/components/LoyaltyPointsDeductionInfoModal'
import { LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE, LOYALTY_DISMISS_POINTS_DEDUCTION_MODAL_STORAGE_KEY } from 'loyalty/constants/loyaltyConfig'
import { get as getLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import { dateIsAfterOrSame, dateIsBefore } from 'lib/datetime/dateUtils'
import { LUX_LOYALTY_POINTS_DEDUCTION_DATE } from '@luxuryescapes/lib-lux-loyalty'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import useOrders from 'hooks/Order/useOrders'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'

const HeroSection = styled.div`
  padding: ${rem(28)} 0;

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const TierDivider = styled(Divider)`
  && {
    margin-top: auto;
    margin-bottom: auto;
    height: ${rem(48)};
    opacity: 0.5;

    ${mediaQueryUp.tablet} {
      height: ${rem(80)};
    }
  }

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const HeroContent = styled.div`
  display: grid;
  justify-content: center;
  gap: ${rem(16)};
  text-align: center;

  ${mediaQueryUp.tablet} {
    text-align: unset;
    justify-content: stretch;
    gap: 0;
    grid-template-columns: 1fr 1fr;
  }
`

const FadedLoadingBox = styled(LoadingBox)`
  opacity: 0.3;
`

const FadedTextLoadingBox = styled(TextLoadingBox)`
  opacity: 0.3;
`

const LinkWrapper = styled.div`
  min-height: ${rem(24)};
`

interface Props {
  activeTab: string
}

function LoyaltyAccountLandingPageHero(props: Props) {
  const { activeTab } = props
  const [tier, tierName] = useCurrentLoyaltyTier()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const account = useLoyaltyAccount().data!
  const [pendingPoints, pendingPointsFetching] = useCurrentLoyaltyPendingPoints()
  const [activities, activitiesFetching] = useCurrentLoyaltyActivities()
  const [userOrders, hasFetchedUserOrders] = useOrders()
  const hasDismissedPointsDeductionModal = !!getLocalStorage(LOYALTY_DISMISS_POINTS_DEDUCTION_MODAL_STORAGE_KEY)
  const showModal = useContext(ModalContext)
  const hasLoyaltyPointsDeductionWarningEnabled = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltyPointsDeductionWarning])

  const hasManualPointsDeduction = useMemo<boolean>(() => {
    return activities.some(activity => activity.type === 'points_ratio_adjust')
  }, [activities])

  const hasPendingPointsDeduction = useMemo<boolean>(() => {
    return pendingPoints.items.some(item => dateIsBefore(new Date(item.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE)))
  }, [pendingPoints.items])

  const hasAccrualPointsDeduction = useMemo<boolean>(() => {
    return activities.some(activity => {
      if (activity.type !== 'earn_points') return false
      // Accrual happened after 27th June 2025 AEST
      if (!dateIsAfterOrSame(new Date(activity.date), new Date(LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE))) return false
      const activityOrder = userOrders.find(order => order.id === activity.orderId)
      // Booking date is before the points deduction date
      return activityOrder && dateIsBefore(new Date(activityOrder.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE))
    })
  }, [activities, userOrders])

  const hasPointsDeduction =
    hasManualPointsDeduction ||
    (hasPendingPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled) ||
    (hasAccrualPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled)

  const handlePointsDeductionModal = useCallback(() => (
    showModal(<LoyaltyPointsDeductionInfoModal />)
  ), [showModal])

  const handleViewActivityClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    document.getElementById('loyalty-your-activity')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const isFetching = activitiesFetching || pendingPointsFetching || !hasFetchedUserOrders
  const membershipNumber = `${account.accountNumber}`

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <HeroContent>
          <Group direction="vertical" gap={8}>
            <Group
              direction="horizontal"
              gap={12}
              horizontalAlign="center"
              verticalAlign="center"
              tabletHorizontalAlign="start"
            >
              <LoyaltyProgramNameLogo width={104} />
              <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">{tierName}</LoyaltyTierLabel>
            </Group>
            <Heading variant="heading2">
              Welcome back{firstName ? `, ${firstName}` : ''}
            </Heading>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText variant="medium">
                Membership {membershipNumber}
              </BodyText>
              <CopyToClipboardClickableIcon
                value={membershipNumber}
                ariaLabel="Copy membership number"
              />
            </Group>
            <CSSBreakpoint min="tablet">
              <LinkWrapper>
                {activeTab === 'about' && <TextLink size="medium" block href="#benefits">View progress</TextLink>}
                {activeTab === 'benefits' && <TextLink
                  size="medium"
                  block
                  href="#loyalty-your-activity"
                  onClick={handleViewActivityClick}
                >
                  View activity
                </TextLink>}
              </LinkWrapper>
            </CSSBreakpoint>
          </Group>
          <Group direction="horizontal" verticalAlign="center" gap={12} desktopGap={40}>
            <Group direction="vertical" gap={4} horizontalAlign="center">
              {isFetching && (
                <>
                  <FadedTextLoadingBox width="6ch" typography="body-small" />
                  <FadedTextLoadingBox width="6ch" typography="heading3" />
                  <FadedTextLoadingBox width="12ch" typography="body-small" />
                  <FadedTextLoadingBox width="12ch" typography="body-small" />
                </>
              )}
              {!isFetching && (
                <>
                  <BodyText variant="small" colour={tier === 'platinum' ? 'tier-platinum-dark-contrast' : 'neutral-two'} align="center">
                    Points
                  </BodyText>
                  <Heading variant="heading3" align="center">
                    <FormatDecimal value={account.points} />
                  </Heading>
                  <BodyTextBlock
                    variant="small"
                    colour={tier === 'platinum' ? 'tier-platinum-dark-contrast' : 'neutral-two'}
                    align="center"
                    endIcon={<Tooltip description={<>Pending points may take up to 24hrs to appear</>} placement="bottom"><LineInfoCircleIcon /></Tooltip>}
                  >
                    <FormatDecimal value={pendingPoints.points} /> pending
                  </BodyTextBlock>
                  {hasPointsDeduction && !hasDismissedPointsDeductionModal &&
                    <TextLink
                      size="small"
                      weight="regular"
                      underline
                      block
                      onClick={handlePointsDeductionModal}
                    >
                      What's changed
                    </TextLink>}
                </>
              )}
            </Group>
            <TierDivider className={`tier-${tier}`} kind="primary" direction="vertical" />
            {!isFetching && <LoyaltyAccountStatusSummary />}
            {isFetching && <Group direction="horizontal" verticalAlign="center" gap={20}>
              <FadedLoadingBox width={76} height={76} />
              <div>
                <FadedTextLoadingBox width="12ch" typography="body-small" />
                <FadedTextLoadingBox width="12ch" typography="body-small" />
              </div>
            </Group>}
          </Group>
          <CSSBreakpoint max="mobile">
            <LinkWrapper>
              {activeTab === 'about' && <TextLink size="medium" href="#benefits">View progress</TextLink>}
              {activeTab === 'benefits' && <TextLink
                size="medium"
                href="#loyalty-your-activity"
                onClick={handleViewActivityClick}
              >
                View activity
              </TextLink>}
            </LinkWrapper>
          </CSSBreakpoint>
        </HeroContent>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyAccountLandingPageHero
